import React from 'react'
import { X, Zap, Edit, ArrowRight, Mail, FileText, CheckSquare, User, type LucideIcon } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { NotificationRecord } from '../services/notificationsApi'
import { LEAD_ACTIONS, buildActionPrompt } from '../leadActions'

const ICON_MAP: Record<string, LucideIcon> = { User, Edit, ArrowRight, Mail, FileText, CheckSquare, Zap }

function getLeadLabel(notification: NotificationRecord): string {
  const lead = notification.lead
  if (!lead) return 'New Lead'
  return lead.name || lead.email || `Lead ${lead.id}`
}

interface LeadAlertRowProps {
  notification: NotificationRecord
  onDismiss: (id: string) => void
  onStartAction: (notification: NotificationRecord) => void
  onLeadAction: (notification: NotificationRecord, promptText: string) => void
}

// A clean, quiet list row for a LEAD_ASSIGNED alert — one muted meta line
// instead of an icon per field, and the quick-action chips live directly
// under their own lead's row instead of a second, disconnected section.
export const LeadAlertRow: React.FC<LeadAlertRowProps> = ({ notification, onDismiss, onStartAction, onLeadAction }) => {
  const { currentTheme: t } = useTheme()
  const lead = notification.lead
  const leadLabel = getLeadLabel(notification)
  const metaLine = [lead?.email, lead?.company, lead?.phone].filter(Boolean).join(' · ')
  const enabledActions = LEAD_ACTIONS.filter((a) => a.enabled)

  return (
    <div
      className="rounded-2xl p-4 flex flex-col gap-3"
      style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[13.5px] font-semibold truncate" style={{ color: t.text }}>{leadLabel}</p>
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold shrink-0"
              style={{ backgroundColor: t.infoBackground, color: t.infoText }}
            >
              Lead assigned
            </span>
          </div>
          {metaLine && (
            <p className="text-[12px] truncate mt-0.5" style={{ color: t.mutedText }}>{metaLine}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => onDismiss(notification.id)}
          aria-label="Dismiss"
          className="p-1 rounded-md shrink-0"
          style={{ color: t.mutedText }}
        >
          <X size={14} />
        </button>
      </div>

      <button
        type="button"
        onClick={() => onStartAction(notification)}
        className="flex items-center justify-center gap-1.5 h-8 rounded-lg text-[12px] font-semibold text-white"
        style={{ background: t.accentGradient }}
      >
        <Zap size={12} />
        Start an Action
      </button>

      {enabledActions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {enabledActions.map((action) => {
            const Icon = ICON_MAP[action.icon] ?? Zap
            return (
              <button
                key={action.id}
                type="button"
                title={action.description}
                onClick={() => onLeadAction(notification, buildActionPrompt(action, lead?.name))}
                className="flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-medium whitespace-nowrap"
                style={{ backgroundColor: t.hoverBackground, color: t.accent, border: `1px solid ${t.border}` }}
              >
                <Icon size={11} />
                {action.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
